import {
  Directive,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  Input,
  inject,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { getAsYouType } from 'awesome-phonenumber';
import { filter, Subscription } from 'rxjs';

/** ITU-T E.164 max digits after the leading +. */
const E164_MAX_DIGITS = 15;

@Directive({
  selector: '[interfacePhoneMask]',
  standalone: true,
})
export class PhoneMaskDirective implements OnInit, OnDestroy {
  private readonly ngControl = inject(NgControl, { self: true });
  sub!: Subscription;

  /**
   * When true, keeps a visible country code and formats internationally
   * (e.g. `+1 480-555-1212`, `+44 7700 900123`) via awesome-phonenumber.
   * Default remains the US national mask `(XXX) XXX-XXXX`.
   */
  @Input() showCountryCode = false;

  @Output() valueChanges = new EventEmitter<string>();
  ngOnInit(): void {
    this.sub = this.ngControl.valueChanges
      ?.pipe(filter((v) => v !== null && v !== undefined))
      .subscribe((value) => {
        const formatted = this.formatPhoneNumber(value);

        if (formatted !== value) {
          this.ngControl.control?.setValue(formatted, { emitEvent: false });
        }
        this.valueChanges.emit(formatted);
      }) as Subscription;
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /**
   * Formats a phone number for the active mask mode.
   */
  private formatPhoneNumber(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const raw = String(value);

    if (this.showCountryCode) {
      return this.formatInternational(raw);
    }

    let digits = raw.replace(/\D/g, '');

    // Drop US country code so +1 / 1 prefixes do not consume the 10-digit mask
    if (digits.startsWith('1')) {
      digits = digits.slice(1);
    }

    return this.formatNational(digits.slice(0, 10));
  }

  /**
   * International as-you-type formatting. Numbers entered with `+` keep their
   * country code (1-, 2-, or 3-digit). Bare national digits default to US `+1`.
   */
  private formatInternational(raw: string): string {
    const trimmed = raw.trim();
    const digits = trimmed.replace(/\D/g, '');
    const hadPlus = trimmed.startsWith('+');

    if (!digits) {
      return hadPlus ? '+' : '';
    }

    let toFeed: string;
    if (
      !hadPlus &&
      !(digits.startsWith('1') && digits.length > 1)
    ) {
      // National US entry without country code → prepend +1
      toFeed = `+1${digits}`;
    } else {
      toFeed = `+${digits}`;
    }

    toFeed = `+${toFeed.slice(1).slice(0, E164_MAX_DIGITS)}`;

    // AsYouType('US') still formats non-US numbers once input starts with +
    const asYouType = getAsYouType('US');
    let formatted = '';
    for (const char of toFeed) {
      formatted = asYouType.addChar(char);
    }

    // Drop trailing space after a bare country code (e.g. "+44 ")
    return formatted.replace(/\s+$/, '');
  }

  private formatNational(limitedDigits: string): string {
    if (limitedDigits.length === 0) {
      return '';
    }

    let formatted = `(${limitedDigits.slice(0, 3)}`;

    if (limitedDigits.length > 3) {
      formatted += `) ${limitedDigits.slice(3, 6)}`;
    }

    if (limitedDigits.length > 6) {
      formatted += `-${limitedDigits.slice(6)}`;
    }

    return formatted;
  }
}
